//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import React, { ReactElement } from 'react'
import { Box, Grid, Typography } from '@mui/material'

import { EthAddr } from '@shared/SchemaTypes'
import { OtStoreState, useOtSelector } from '@react/redux/store'
import { PoolOverviewCTO, PoolOverviewCTOSchema } from '@shared/sharedTypes'
import ViewField from '@react/components/elements/ViewField'
import { RolloverTypeEnum } from '@shared/Enums'
import { erc20WeiToCommaDecString } from '@shared/conversions'
import { LoanCTOSchema } from '@shared/LoanCTO'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'

export default function BorrowerRolloverTransfer(props: { poolAddr: EthAddr }): ReactElement {
  const { poolAddr } = props

  const poolOverviewCTO = useOtSelector(
    (state: OtStoreState) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO] as PoolOverviewCTO
  )
  if (!poolOverviewCTO) return <></>

  //   let maturingLoanState: LoanCTO
  //   let startingLoanState: LoanCTO
  //
  // let assetsToReturnToPool:number = 0
  // let startingLoanPrincipal: number = 0
  //   let maturingLoanEndingValue: number = 0
  //   let assetsToGetFromPool: number = 0
  //
  //   if (poolOverviewCTO.maturedLoanState.loanAddr === ETH_ADDR_ZERO) {
  //     logOT.info('started rollover  poolOverviewCTO',poolOverviewCTO)
  //     maturingLoanState = poolOverviewCTO.activeLoanState
  //     startingLoanState = poolOverviewCTO.requestedLoanState
  //     assetsToReturnToPool = Number(poolOverviewCTO.totalRequestedAssets) - Number(poolOverviewCTO.poolBalance)
  //     logOT.info('assetsToReturnToPool', assetsToReturnToPool,'=', poolOverviewCTO.totalRequestedAssets, '-', poolOverviewCTO.poolBalance)
  //     maturingLoanEndingValue =  (Number(maturingLoanState.principal)/ 1e18) * Number(maturingLoanState.exchangeRateAtMaturity)
  //     logOT.info('maturingLoanEndingValue',maturingLoanEndingValue)
  //     startingLoanPrincipal = maturingLoanEndingValue -  Number(poolOverviewCTO.totalRequestedAssets) +Number(poolOverviewCTO.poolBalance)
  //     logOT.info('startingLoanPrincipal', startingLoanPrincipal,'=',maturingLoanEndingValue, '-', Number(poolOverviewCTO.totalRequestedAssets),'+',Number(poolOverviewCTO.poolBalance))
  //     if (assetsToReturnToPool < 0){
  //       assetsToGetFromPool = 0-assetsToReturnToPool
  //       assetsToReturnToPool = 0
  //
  //     }
  //   } else {
  //     maturingLoanState = poolOverviewCTO.maturedLoanState
  //     startingLoanState = poolOverviewCTO.activeLoanState
  //     assetsToReturnToPool = Number(maturingLoanState.assetsToReturnToPool)
  //     assetsToGetFromPool = Number(startingLoanState.assetsFromPool)
  //     startingLoanPrincipal = Number(startingLoanState.principal)
  //     maturingLoanEndingValue = (Number(maturingLoanState.principal)/ 1e18) * Number(maturingLoanState.exchangeRateAtMaturity)
  //   }
  //   const totalLoanEndingValue = Number(maturingLoanState.principal) + Number(maturingLoanState.interest)
  //   const repaymentAmount = assetsToReturnToPool + Number(maturingLoanState.originationFee)
  //   const totalNextLoanPrincipal = Number(startingLoanState.assetsFromPriorLoan) + Number(startingLoanState.assetsFromPool)
  //
  //
  //   const calculatedCTO = {
  //     totalLoanEndingValue,
  //     repaymentAmount,
  //     totalNextLoanPrincipal
  //   }

  let titleString: string
  if (poolOverviewCTO.rolloverType === RolloverTypeEnum.NetAdditionalDeposits) {
    titleString = `Net Additional Deposits of ${erc20WeiToCommaDecString(
      poolOverviewCTO.rolloverAssetToGetFromPool
    )} ${poolOverviewCTO.liquidityTokenSymbol}; Repayment of Fees of ${erc20WeiToCommaDecString(
      poolOverviewCTO.rolloverMaturingLoanFees
    )} ${poolOverviewCTO.liquidityTokenSymbol}`
  } else if (poolOverviewCTO.rolloverType === RolloverTypeEnum.NeedsFullRepayment) {
    titleString = `Full Repayment of ${erc20WeiToCommaDecString(
      poolOverviewCTO.rolloverNetRepaymentWithFees
    )} ${poolOverviewCTO.liquidityTokenSymbol}`
  } else {
    titleString = `Partial Repayment of ${erc20WeiToCommaDecString(
      poolOverviewCTO.rolloverNetRepaymentWithFees
    )} ${poolOverviewCTO.liquidityTokenSymbol}`
  }

  return (
    <Box
      data-testid={'BorrowerRolloverTransfer'}
      px={0}
      py={2}
      sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', direction: 'column' }}
    >
      <Grid container spacing={12} columns={24}>
        <Grid item xs={24}>
          <Typography variant={'h6'}>{titleString}</Typography>
        </Grid>

        <Grid item xs={7}>
          <ViewField
            dataObject={poolOverviewCTO}
            fieldSchema={PoolOverviewCTOSchema.describe().fields.rolloverClosingLoanEndingValue}
            toolTip="The total value of the principal and accrued interest at maturity for the current active loan."
          />
        </Grid>
        <Grid item xs={1}>
          <Typography variant={'h6'}>-</Typography>
        </Grid>
        <Grid item xs={7}>
          <ViewField
            dataObject={poolOverviewCTO}
            fieldSchema={PoolOverviewCTOSchema.describe().fields.totalRequestedAssets}
            toolTip="The total amount of withdrawal requests for the current active loan cycle that have yet to be repaid, including both rollover terminations and early withdrawals."
          />
        </Grid>

        <Grid item xs={1}>
          <Typography variant={'h6'}>+</Typography>
        </Grid>
        <Grid item xs={7}>
          <ViewField
            dataObject={poolOverviewCTO}
            fieldSchema={PoolOverviewCTOSchema.describe().fields.poolBalance}
            toolTip="The total amount of USDC that is currently in the Loan Vault. This USDC has either been deposited by lenders or repaid by the borrower."
          />
        </Grid>
        <Grid item xs={6}>
          {poolOverviewCTO.rolloverState === 3 && (
            <ViewField
              liquidityTokenSymbol={poolOverviewCTO.liquidityTokenSymbol}
              dataObject={poolOverviewCTO.requestedLoanState}
              fieldSchema={LoanCTOSchema.describe().fields.assetsFromPool}
              toolTip="The total amount of USDC that is has transfered from the Loan Vault."
            />
          )}
        </Grid>
        <Grid item xs={1}>
          <Typography variant={'h6'}>-</Typography>
        </Grid>
        <Grid item xs={6}>
          <ViewField
            dataObject={poolOverviewCTO}
            fieldSchema={PoolOverviewCTOSchema.describe().fields.poolAssetsForWithdraw}
            toolTip="The total amount of USDC that is currently in the Loan Vault. This USDC has either been deposited by lenders or repaid by the borrower."
          />
        </Grid>

        <Grid item xs={1}>
          <Typography variant={'h6'}>=</Typography>
        </Grid>
        <Grid item xs={6}>
          <ViewField
            dataObject={poolOverviewCTO}
            fieldSchema={PoolOverviewCTOSchema.describe().fields.rolloverOpeningLoanPrincipal}
            toolTip="The principal amount of the next rollover loan. This is equal to the amount of loan collateral to be purchased by the Borrower before or on the rollover date."
          />
        </Grid>
        <Grid item xs={7}>
          <ViewField
            dataObject={poolOverviewCTO}
            fieldSchema={PoolOverviewCTOSchema.describe().fields.rolloverAssetsToReturnToPool}
            toolTip="The amount of USDC to be sent back into the Loan Vault by the Borrower to meet rollover terminations. This amount does not include Fees. This needs to be deposited in the Borrower Vault before the Payment Date (2 business days after maturity)."
          />
        </Grid>
        <Grid item xs={1}>
          <Typography variant={'h6'}>+</Typography>
        </Grid>
        <Grid item xs={7}>
          <ViewField
            dataObject={poolOverviewCTO}
            fieldSchema={PoolOverviewCTOSchema.describe().fields.rolloverMaturingLoanFees}
            toolTip="The amount of fees due at maturity. Fees are to be paid by the borrower in USDC. The Fee amount is the sum of the Platform Fee and Advisory Fee."
          />
        </Grid>
        <Grid item xs={1}>
          <Typography variant={'h6'}>=</Typography>
        </Grid>
        <Grid item xs={7}>
          <ViewField
            dataObject={poolOverviewCTO}
            fieldSchema={PoolOverviewCTOSchema.describe().fields.rolloverNetRepaymentWithFees}
            toolTip="The amount of USDC to be sent back into the Borrower Vault by the Borrower to meet rollover terminations and pay Fees."
          />
        </Grid>
        <Grid item xs={8}>
          <ViewField
            dataObject={poolOverviewCTO}
            fieldSchema={PoolOverviewCTOSchema.describe().fields.rolloverAssetsFromPriorLoan}
            toolTip="The amount of principal and interest that is to be compounded and rolled over into the next loan. This amount should be reinvested in loan collateral."
          />
        </Grid>
        <Grid item xs={8}>
          <ViewField
            dataObject={poolOverviewCTO}
            fieldSchema={PoolOverviewCTOSchema.describe().fields.rolloverAssetToGetFromPool}
            toolTip="The amount of USDC to be drawn from the Loan Vault by the borrower during loan rollover. This amount must be converted from USDC to USD during the Transfer In Window. This number is only positive for Net Inflows."
          />
        </Grid>
      </Grid>
    </Box>
  )
}
